import React, { PropsWithChildren } from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';

import { ApiUrls } from '@app/AppConstants/ApiUrls';
import { PersonLookupModel } from '@app/Models/WebApiModels';

import ApiService from '@app/Services/ApiService';
import DateTimeService from '@app/Services/DateTimeService';
import { permissionStore } from '@app/Stores/PermissionStore';
import { applyDecorators } from '@app/Helpers/Decorator';
import { devInfoStore } from '@app/Stores/DevInfoStore';
import { appStore } from '@app/AppStore';

import AircraftPlaceholder from './AircraftPlaceholder';

type ErrorBoundaryState = {
    hasError: boolean;
    error: Error | null;
    componentStack: string | null;
    timestamp: Date | null;
    impersonatePersonName?: string | null;
};

type ErrorBoundaryProps = PropsWithChildren & {
    className?: string;
};

@observer
export default class ErrorBoundary extends React.Component<ErrorBoundaryProps> {
    @observable errorState: ErrorBoundaryState = {
        hasError: false,
        error: null,
        componentStack: null,
        timestamp: null,
        impersonatePersonName: null
    };

    constructor(props: ErrorBoundaryProps) {
        super(props);
        applyDecorators(this);
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        this.errorState = {
            hasError: true,
            error: error,
            componentStack: errorInfo.componentStack || null,
            timestamp: DateTimeService.now()
        };
        void this._getImpersonatePersonName();
    }

    render() {
        if (this.errorState.hasError) {
            const { error, componentStack, timestamp } = this.errorState;
            const message: string = error ? error.message : 'Error';

            const realUserRoles = permissionStore.getRolesMatchFromEncodedRoles(appStore.currentUser?.roles || []);
            const impersonatedUserRoles = permissionStore.getRolesMatchFromEncodedRoles(appStore.impersonatedUser?.roles || []);

            return (
                <AircraftPlaceholder pageTitle="Something went wrong" mainClass="error-boundary">
                    <h2 className="scene-message-red">Client rendering error</h2>
                    <p>Please contact support.</p>
                    <p className="scene-message-red tal">{message}</p>
                    {error && error.stack && <p className="scene-message-red tal">{error.stack}</p>}
                    {componentStack && <p className="scene-message-red tal">
                        The above error occurred in:
                        {componentStack}
                    </p>}
                    <h3 className="scene-message-additional">Technical details</h3>
                    {devInfoStore.appVersion && (
                        <p className="tal">
                            <small>
                                <strong>Version:</strong> {devInfoStore.appEnvironment} - {devInfoStore.appVersion}
                            </small>
                        </p>
                    )}
                    {appStore.currentUser && (
                        <p className="tal">
                            <small>
                                <strong>Principal claims:</strong> {realUserRoles}
                            </small>
                        </p>
                    )}
                    {appStore.impersonatedUser && appStore.impersonatedUser.personId && (
                        <p className="tal">
                            <small>
                                <strong>Impersonate person:</strong> {this.errorState.impersonatePersonName}
                                {appStore.impersonatedUser.impersonateAsPrincipal ? ' (as principal)' : ''}
                            </small>
                        </p>
                    )}
                    {appStore.impersonatedUser && appStore.impersonatedUser.roles && (
                        <p className="tal">
                            <small>
                                <strong>Impersonate claims:</strong> {impersonatedUserRoles}
                            </small>
                        </p>
                    )}
                    {timestamp && (
                        <p className="tal">
                            <small>
                                <strong>Timestamp:</strong> {DateTimeService.format(timestamp, 'YYYY-MM-DD HH:mm:ss')}
                            </small>
                        </p>
                    )}
                </AircraftPlaceholder>
            );
        }

        return this.props.children;
    }

    private async _getImpersonatePersonName() {
        if (!appStore.impersonatedUser || !appStore.impersonatedUser.personId) return;

        const { data: persons } = await ApiService.postTypedData<PersonLookupModel[]>(ApiUrls.PersonLookupUrl, {
            ids: [appStore.impersonatedUser.personId]
        });

        if (persons.length) {
            this._setPersonName(persons[0].firstName, persons[0].lastName);
        }
    }

    private _setPersonName(firstName?: string, lastName?: string) {
        if (appStore.impersonatedUser && appStore.impersonatedUser.personId) {
            const personName = `${firstName || ''} ${lastName || ''} (${this._trimStartEndZero(appStore.impersonatedUser.personId)})`;
            
            this.errorState = {
                ...this.errorState,
                impersonatePersonName: personName
            };
        }
    }

    private _trimStartEndZero(personId: string) {
        return personId.replace(new RegExp('^[0, -]+'), '').replace(new RegExp('[0, -]+$'), '');
    }
}